import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';
import Container from '../components/ui/Container';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import SectionHeader from '../components/ui/SectionHeader';
import ProductCard from '../components/sections/ProductCard';
import {
  getProductBySlug,
  getRelatedProducts,
  getThemeLabel,
  legacySlugRedirects,
} from '../data/products';
import './ProductDetail.css';

const STATUS_LABEL = {
  live: '서비스 중',
  beta: '베타',
  wip: '개발 중',
};

const ProductDetail = () => {
  const { slug } = useParams();
  const redirectSlug = legacySlugRedirects[slug];
  const product = getProductBySlug(slug);
  const related = getRelatedProducts(slug, 3);

  useEffect(() => {
    // 외부 서브도메인이 지정된 프로덕트는 즉시 외부 URL로 리다이렉트 (같은 탭)
    if (product?.externalUrl) {
      window.location.replace(product.externalUrl);
      return;
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug, product]);

  // 개편 전 개별 프로덕트 slug로 들어온 유입은 통합된 프로덕트로 보낸다
  if (redirectSlug) {
    return <Navigate to={`/products/${redirectSlug}`} replace />;
  }

  if (!product) {
    return <Navigate to="/products" replace />;
  }

  // 리다이렉트 직전, 화면이 빈 채로 남지 않도록 짧은 안내
  if (product.externalUrl) {
    return (
      <div className="pdp pdp--redirecting" role="status" aria-live="polite">
        <div className="pdp-redirecting__inner">
          <span className="pdp-redirecting__icon" aria-hidden="true">{product.icon}</span>
          <p className="pdp-redirecting__title">
            {product.nameKo} 페이지로 이동 중입니다…
          </p>
          <p className="pdp-redirecting__sub">
            자동으로 이동하지 않으면{' '}
            <a href={product.externalUrl} rel="noopener noreferrer">
              여기를 클릭
            </a>
            해주세요.
          </p>
        </div>
      </div>
    );
  }

  const themeLabel = getThemeLabel(product.theme);

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.tagline,
    brand: { '@type': 'Brand', name: 'Flowto.AI' },
    category: themeLabel,
  };

  return (
    <div className="pdp" style={{ '--pdp-color': product.color }}>
      <SEO
        title={`${product.nameKo} — ${product.tagline}`}
        description={product.description}
        url={`/products/${product.slug}`}
        structuredData={productSchema}
      />

      {/* Hero */}
      <section className="pdp-hero">
        <div className="pdp-hero__bg" aria-hidden="true" />
        <Container>
          <nav className="pdp-breadcrumb" aria-label="breadcrumb">
            <Link to="/products">Products</Link>
            <span aria-hidden="true">/</span>
            <span>{themeLabel}</span>
            <span aria-hidden="true">/</span>
            <span>{product.nameKo}</span>
          </nav>

          <div className="pdp-hero__grid">
            <div className="pdp-hero__copy">
              <div className="pdp-hero__meta">
                <Badge variant="primary" size="md">{themeLabel}</Badge>
                <Badge variant={product.status === 'live' ? 'success' : 'warning'} size="md">
                  {STATUS_LABEL[product.status] ?? product.status}
                </Badge>
              </div>
              <h1 className="pdp-hero__title">
                <span className="pdp-hero__name-ko">{product.nameKo}</span>
                <span className="pdp-hero__name-en">{product.name}</span>
              </h1>
              <p className="pdp-hero__tagline">{product.tagline}</p>
              <p className="pdp-hero__desc">{product.description}</p>
              <div className="pdp-hero__cta">
                {product.storeUrl ? (
                  <Button
                    as="a"
                    href={product.storeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="primary"
                    size="lg"
                    pill
                  >
                    Google Play에서 받기
                  </Button>
                ) : (
                  <span className="pdp-hero__note">
                    아직 개발 중입니다. 출시되면 이 페이지에 링크가 올라옵니다.
                  </span>
                )}
                <Button
                  as={Link}
                  to="/contact"
                  variant="outline"
                  size="lg"
                  pill
                >
                  문의하기
                </Button>
              </div>
            </div>

            <div className="pdp-hero__visual" aria-hidden="true">
              {product.screenshots?.length ? (
                product.screenshots.slice(0, 2).map((src, i) => (
                  <figure key={src} className={`pdp-hero__shot pdp-hero__shot--${i + 1}`}>
                    <img
                      src={src}
                      alt=""
                      width={product.screenshotSizes?.[i]?.width}
                      height={product.screenshotSizes?.[i]?.height}
                      loading="eager"
                    />
                  </figure>
                ))
              ) : (
                <div className="pdp-hero__icon-wrap">
                  <span className="pdp-hero__icon">{product.icon}</span>
                </div>
              )}
            </div>
          </div>
        </Container>
      </section>

      {/* 통합 앱에 들어간 미니 도구 */}
      {product.subApps && (
        <section className="pdp-section">
          <Container>
            <SectionHeader
              eyebrow="Inside"
              title="이런 도구가 들어 있습니다"
            />
            <ul className="pdp-subapps">
              {product.subApps.map((s) => (
                <li key={s.name} className="pdp-subapps__item">
                  <h3 className="pdp-subapps__name">{s.name}</h3>
                  <p className="pdp-subapps__desc">{s.desc}</p>
                </li>
              ))}
            </ul>
          </Container>
        </section>
      )}

      {/* Features */}
      {product.features && (
        <section className="pdp-section pdp-section--alt">
          <Container>
            <SectionHeader
              eyebrow="Features"
              align="center"
              title="핵심 기능"
            />
            <ul className="pdp-features">
              {product.features.slice(0, 4).map((f, i) => (
                <li key={f.title} className="pdp-features__item">
                  <span className="pdp-features__num" aria-hidden="true">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3 className="pdp-features__title">{f.title}</h3>
                  <p className="pdp-features__desc">{f.desc}</p>
                </li>
              ))}
            </ul>
          </Container>
        </section>
      )}

      {/* Related products */}
      <section className="pdp-section">
        <Container>
          <SectionHeader
            eyebrow="Other Products"
            title="다른 프로덕트도 보세요"
          />
          <div className="pdp-related">
            {related.map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} />
            ))}
          </div>
          <div className="pdp-related__more">
            <Button
              as={Link}
              to="/products"
              variant="ghost"
              size="md"
              pill
              iconRight={<ArrowRight size={16} strokeWidth={2.5} />}
            >
              전체 프로덕트 보기
            </Button>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default ProductDetail;
